// Layout pour les pages du parcours diagnostic
// Combine Header, ProgressSidebar et zone de contenu
// Refactored: All data via REST API only (no offlineDB/IndexedDB)

import { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Header from './Header'
import ProgressSidebar, { ProgressBarMobile, type Step, type StepStatus } from './ProgressSidebar'
import { SidebarProvider, SidebarInset } from '../ui/sidebar'
import { Button } from '../ui'
import { api } from '../../services/api'

// State de navigation pour le diagnostic (métiers, etc.)
export interface DiagnosticNavigationState {
  metierIds?: string[]
  metiersSelectionnes?: Array<{ code: string; nom: string; ordre: number }>
  selectedThemes?: string[]
  currentMetierIndex?: number // Index du métier courant pour la navigation Q1
  metierCode?: string // Code du métier courant
  // Autres données à passer entre pages
}

interface DiagnosticLayoutProps {
  children: ReactNode
  // Étapes de progression
  steps: Step[]
  // Navigation
  onBack?: () => void
  onNext?: () => void
  backLabel?: string
  nextLabel?: string
  nextDisabled?: boolean
  showNavigation?: boolean
  // Header
  showOnlineStatus?: boolean
  isOnline?: boolean
  // Navigation via stepper
  onStepClick?: (step: Step) => void
  // State à passer lors de la navigation via stepper
  diagnosticState?: DiagnosticNavigationState
}

export default function DiagnosticLayout({
  children,
  steps,
  onBack,
  onNext,
  backLabel = 'Retour',
  nextLabel = 'Suivant',
  nextDisabled = false,
  showNavigation = true,
  showOnlineStatus = false,
  isOnline = true,
  onStepClick,
  diagnosticState,
}: DiagnosticLayoutProps) {
  const navigate = useNavigate()

  // Handler par défaut pour la navigation via stepper
  const handleStepClick = (step: Step) => {
    if (onStepClick) {
      onStepClick(step)
    } else if (step.route) {
      // Fusionner step.data avec diagnosticState
      let navigationState: Record<string, unknown> = { ...diagnosticState }

      if (step.data) {
        // Clic sur enfant → utiliser ses données (ex: currentMetierIndex)
        navigationState = { ...navigationState, ...step.data }
      } else if (step.children && step.children.length > 0) {
        // Clic sur parent avec enfants → naviguer vers le premier enfant
        navigationState = { ...navigationState, currentMetierIndex: 0 }
      }

      // Ajouter un timestamp pour forcer React Router à détecter le changement d'état
      navigationState._navTimestamp = Date.now()

      navigate(step.route, { state: navigationState, replace: false })
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigate(-1)
    }
  }

  return (
    <SidebarProvider>
      {/* Header mobile uniquement */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-20">
        <Header
          showOnlineStatus={showOnlineStatus}
          isOnline={isOnline}
          showHelp={false}
          showAccount={false}
        />
        <ProgressBarMobile steps={steps} />
      </div>

      {/* Sidebar desktop (shadcn Sidebar — identique à AppSidebar) */}
      <ProgressSidebar steps={steps} onStepClick={handleStepClick} />

      {/* Contenu principal */}
      <SidebarInset>
        <div className="min-h-svh flex flex-col bg-background">
          {/* Spacer mobile pour le header fixe */}
          <div className="md:hidden h-24" />

          {/* Contenu scrollable */}
          <div className="flex-1 flex flex-col p-4 sm:p-6 md:px-8 md:py-6 xl:px-12 2xl:px-16">
            {children}
          </div>

          {/* Navigation fixe en bas */}
          {showNavigation && (
            <div className="sticky bottom-0 bg-card border-t border-border p-4 sm:p-6 md:px-8 xl:px-12 2xl:px-16">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="size-4" />
                  {backLabel}
                </Button>

                <Button
                  onClick={onNext}
                  disabled={nextDisabled || !onNext}
                  className="flex items-center gap-2"
                >
                  {nextLabel}
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

// Options pour la progression multi-métiers
interface MultiMetierProgress {
  totalMetiers: number           // Nombre total de métiers sélectionnés
  currentMetierIndex: number     // Index du métier en cours (0, 1, 2)
  q2Completed: boolean           // Q2 a-t-il déjà été complété ?
}

// Progression réelle du diagnostic (chargée depuis l'API)
export interface DiagnosticProgress {
  hasQ1Responses: boolean        // Des réponses Q1 existent
  hasQ2Responses: boolean        // Des réponses Q2 existent
  hasResults: boolean            // Des résultats ont été calculés
  hasSelectedThemes: boolean     // Des thèmes ont été sélectionnés
  hasSelectedSolutions: boolean  // Des solutions ont été sélectionnées
  hasActions: boolean            // Des actions ont été créées
  isTerminated: boolean          // Diagnostic terminé
  metiersWithQ1Responses: string[] // IDs des métiers avec réponses Q1
}

// Hook pour charger la progression réelle du diagnostic depuis l'API
export function useDiagnosticProgress(diagnosticId: string | undefined): DiagnosticProgress | null {
  const [progress, setProgress] = useState<DiagnosticProgress | null>(null)

  useEffect(() => {
    async function loadProgress() {
      if (!diagnosticId) {
        setProgress(null)
        return
      }

      try {
        // Load full diagnostic from API (includes reponses, resultat)
        const diagnostic = await api.getDiagnostic(diagnosticId)

        const reponses = diagnostic.reponses || []

        // Séparer les réponses Q1 et Q2
        const q1Responses = reponses.filter((r: any) => r.metierId)
        const q2Responses = reponses.filter((r: any) => !r.metierId)

        // Extraire les métiers qui ont des réponses Q1
        const metiersWithQ1 = [...new Set(q1Responses.map((r: any) => r.metier?.code || r.metierId).filter(Boolean) as string[])]

        // Vérifier si des actions existent
        let hasActions = false
        try {
          const actions = await api.getActionPlans(diagnosticId)
          hasActions = Array.isArray(actions) && actions.length > 0
        } catch {
          // Pas grave si ça échoue — juste un indicateur de progression
        }

        setProgress({
          hasQ1Responses: q1Responses.length > 0,
          hasQ2Responses: q2Responses.length > 0,
          hasResults: !!diagnostic.resultat,
          hasSelectedThemes: Array.isArray(diagnostic.selectedThemes) && diagnostic.selectedThemes.length > 0,
          hasSelectedSolutions: Array.isArray(diagnostic.selectedSolutions) && diagnostic.selectedSolutions.length > 0,
          hasActions,
          isTerminated: diagnostic.statut === 'termine',
          metiersWithQ1Responses: metiersWithQ1,
        })
      } catch (error) {
        console.error('Erreur chargement progression diagnostic:', error)
        setProgress(null)
      }
    }

    loadProgress()
  }, [diagnosticId])

  return progress
}

// Interface étendue pour les infos multi-métiers avec noms
export interface MultiMetierProgressExtended extends MultiMetierProgress {
  metiersSelectionnes?: Array<{ code: string; nom: string }>
  currentMetierCode?: string
}

// Hook pour générer les étapes standard du diagnostic
export function useDiagnosticSteps(
  currentStep: 'identification' | 'dashboard' | 'metiers' | 'situations-travail' | 'questionnaire-q1' | 'questionnaire-q2' | 'resultats' | 'themes' | 'solutions' | 'plan-actions' | 'fin' | 'attestation' | 'synthese-finale',
  diagnosticId?: string,
  multiMetierProgress?: MultiMetierProgressExtended,
  diagnosticProgress?: DiagnosticProgress | null,
  currentSubStep?: string // Pour identifier la sous-page active (ex: 'resultats-scores', 'q1-metier-ELE')
): Step[] {
  // Aliases pour rétrocompatibilité (ces pages existent encore mais ne sont plus dans le stepper)
  const normalizedStep = currentStep === 'identification' || currentStep === 'dashboard' ? 'metiers'
    : currentStep === 'plan-actions' ? 'fin'
    : currentStep
  // 'fin' est la dernière étape du stepper (attestation et synthèse-finale sont hors parcours principal)
  const stepOrder = ['metiers', 'situations-travail', 'questionnaire-q1', 'questionnaire-q2', 'resultats', 'themes', 'solutions', 'fin']
  const currentIndex = stepOrder.indexOf(normalizedStep)

  // Extraire les infos multi-métiers
  const totalMetiers = multiMetierProgress?.totalMetiers || 1
  const currentMetierIndex = multiMetierProgress?.currentMetierIndex || 0
  const q2Completed = multiMetierProgress?.q2Completed || false
  const isMultiMetier = totalMetiers > 1
  const metiersSelectionnes = multiMetierProgress?.metiersSelectionnes || []
  const currentMetierCode = multiMetierProgress?.currentMetierCode

  // Métiers avec réponses Q1 (depuis l'API)
  const metiersWithQ1Responses = diagnosticProgress?.metiersWithQ1Responses || []

  // Helper pour vérifier si une étape a des données réelles
  const checkStepHasData = (stepId: string): boolean => {
    if (!diagnosticProgress) return false
    switch (stepId) {
      case 'metiers':
        return true // Si on a un diagnosticId, les métiers ont été sélectionnés
      case 'situations-travail':
        return diagnosticProgress.hasQ1Responses // On passe par là pour arriver à Q1
      case 'questionnaire-q1':
        return diagnosticProgress.hasQ1Responses
      case 'questionnaire-q2':
        return diagnosticProgress.hasQ2Responses
      case 'resultats':
        // Résultats est "complété" si on a des résultats sauvegardés
        // OU si Q2 est complété ET tous les métiers ont été évalués
        const allMetiersEvaluated = totalMetiers > 0
          && metiersWithQ1Responses.length >= totalMetiers
        return diagnosticProgress.hasResults || (diagnosticProgress.hasQ2Responses && allMetiersEvaluated)
      case 'themes':
        return diagnosticProgress.hasSelectedThemes || diagnosticProgress.hasActions
      case 'solutions':
        return diagnosticProgress.hasSelectedSolutions || diagnosticProgress.hasActions
      case 'fin':
        return diagnosticProgress.hasSelectedSolutions || diagnosticProgress.hasActions
      default:
        return false
    }
  }

  // Générer la description du questionnaire Q1 avec progression métiers
  const getQ1Description = () => {
    if (!isMultiMetier) {
      return 'Évaluez vos pratiques de prévention des risques'
    }
    // Compter les métiers évalués via API data
    const evaluatedCount = metiersWithQ1Responses.length
    if (evaluatedCount === totalMetiers) {
      return `${totalMetiers}/${totalMetiers} métiers évalués`
    }
    if (normalizedStep === 'questionnaire-q1') {
      return `Métier ${currentMetierIndex + 1}/${totalMetiers} - Évaluez vos pratiques`
    }
    return `${evaluatedCount}/${totalMetiers} métiers évalués`
  }

  // Générer les sous-étapes pour les métiers (Q1)
  const generateMetierChildren = (): Step[] | undefined => {
    if (!isMultiMetier || metiersSelectionnes.length === 0) return undefined

    return metiersSelectionnes.map((metier, index) => {
      const hasResponses = metiersWithQ1Responses.includes(metier.code)
      const isCurrent = currentMetierCode === metier.code && normalizedStep === 'questionnaire-q1'

      let status: StepStatus
      if (hasResponses) {
        status = 'completed'
      } else if (isCurrent) {
        status = 'current'
      } else {
        status = 'upcoming'
      }

      return {
        id: `q1-metier-${metier.code}`,
        title: metier.nom,
        description: `Métier ${index + 1}/${totalMetiers}`,
        status,
        route: diagnosticId ? `/diagnostic/${diagnosticId}/q1` : undefined,
        data: { currentMetierIndex: index, metierCode: metier.code },
      }
    })
  }

  // Générer les sous-étapes pour les résultats
  const generateResultsChildren = (): Step[] | undefined => {
    if (!diagnosticId) return undefined

    const resultsSubSteps = [
      { id: 'resultats-scores', title: 'Scores et radars', description: 'Axes HALTES et Management', path: 'resultats' },
      { id: 'resultats-analyse', title: 'Analyse détaillée', description: 'Points forts et axes d\'amélioration', path: 'synthese-2' },
      { id: 'resultats-casques', title: 'Niveau de prévention', description: 'Votre score en casques', path: 'casques' },
    ]

    return resultsSubSteps.map((subStep, index) => {
      const isCurrent = currentSubStep === subStep.id
      // Les sous-étapes précédentes sont complétées si on est sur une sous-étape suivante
      const currentSubIndex = resultsSubSteps.findIndex(s => s.id === currentSubStep)
      const isCompleted = currentSubIndex > index

      // Vérifier si une étape après "Résultats" a des données (implique que toutes les sous-étapes ont été vues)
      const postResultsStepCompleted = checkStepHasData('themes') || checkStepHasData('solutions') || checkStepHasData('fin')

      let status: StepStatus
      if (isCurrent) {
        status = 'current'
      } else if (isCompleted || postResultsStepCompleted || (normalizedStep !== 'resultats' && checkStepHasData('resultats'))) {
        status = 'completed'
      } else {
        status = 'upcoming'
      }

      return {
        id: subStep.id,
        title: subStep.title,
        description: subStep.description,
        status,
        route: `/diagnostic/${diagnosticId}/${subStep.path}`,
      }
    })
  }

  // Générer les routes dynamiques basées sur diagnosticId
  const getRoute = (stepId: string): string | undefined => {
    if (!diagnosticId) return undefined
    const routes: Record<string, string> = {
      'metiers': '/diagnostic/nouveau',
      'situations-travail': `/diagnostic/${diagnosticId}/situations`,
      'questionnaire-q1': `/diagnostic/${diagnosticId}/q1`,
      'questionnaire-q2': `/diagnostic/${diagnosticId}/q2`,
      'resultats': `/diagnostic/${diagnosticId}/resultats`,
      'themes': `/diagnostic/${diagnosticId}/themes`,
      'solutions': `/diagnostic/${diagnosticId}/solutions`,
      'fin': `/diagnostic/${diagnosticId}/finalisation`,
    }
    return routes[stepId]
  }

  const stepsConfig = [
    {
      id: 'metiers',
      title: 'Vos métiers',
      description: 'Sélectionnez vos métiers',
    },
    {
      id: 'situations-travail',
      title: 'Situations à risque',
      description: 'Prenez connaissance des risques prioritaires',
    },
    {
      id: 'questionnaire-q1',
      title: 'Questionnaire Risques',
      description: getQ1Description(),
    },
    {
      id: 'questionnaire-q2',
      title: 'Questionnaire Management',
      description: q2Completed || diagnosticProgress?.hasQ2Responses ? 'Complété' : 'Évaluez votre organisation de la prévention',
    },
    {
      id: 'resultats',
      title: 'Résultats',
      description: 'Consultez votre score et vos axes d\'amélioration',
    },
    {
      id: 'themes',
      title: 'Choix des thèmes',
      description: 'Sélectionnez les thèmes à améliorer',
    },
    {
      id: 'solutions',
      title: 'Catalogue des solutions',
      description: 'Choisissez vos actions parmi les solutions',
    },
    {
      id: 'fin',
      title: 'Finalisation',
      description: 'Exports et plan d\'action',
    },
  ]

  return stepsConfig.map((step, index) => {
    // Logique de détermination du statut :
    // 1. Étape actuelle → 'current'
    // 2. Étapes AVANT la position actuelle → 'completed'
    // 3. Étapes APRÈS la position actuelle → vérifier diagnosticProgress
    //    - Si des données existent → 'completed'
    //    - Sinon → 'upcoming'

    let status: StepStatus

    if (index === currentIndex) {
      // Étape actuelle
      status = 'current'
    } else if (index < currentIndex) {
      // Étapes avant la position actuelle → toujours completed
      status = 'completed'
    } else {
      // Étapes après la position actuelle → vérifier les données réelles
      if (checkStepHasData(step.id)) {
        status = 'completed'
      } else {
        status = 'upcoming'
      }
    }

    // Générer les enfants pour certaines étapes
    let children: Step[] | undefined
    if (step.id === 'questionnaire-q1') {
      children = generateMetierChildren()
    } else if (step.id === 'resultats') {
      children = generateResultsChildren()
    }

    return {
      ...step,
      status,
      route: getRoute(step.id),
      children,
    }
  })
}

// Interface pour les métiers sélectionnés
interface SelectedMetier {
  code: string
  nom: string
  ordre: number
}

// Hook pour charger les données du diagnostic depuis location.state ou l'API
export function useDiagnosticData(diagnosticId: string | undefined) {
  const location = useLocation()

  // State initial depuis location.state
  const [metierIds, setMetierIds] = useState<string[]>(
    location.state?.metierIds || []
  )
  const [metiersSelectionnes, setMetiersSelectionnes] = useState<SelectedMetier[]>(
    location.state?.metiersSelectionnes || []
  )
  const [isLoadingData, setIsLoadingData] = useState(false)

  // Si pas de données dans le state, charger depuis l'API
  useEffect(() => {
    async function loadFromAPI() {
      if (metierIds.length > 0 || !diagnosticId) return

      setIsLoadingData(true)
      try {
        const diagnostic = await api.getDiagnostic(diagnosticId)

        // Extract metierIds from the diagnostic (codes, pas cuids)
        const diagMetierIds = diagnostic.metierIds ||
          (diagnostic.metiers || []).map((m: any) => m.metier?.code || m.metierId)

        if (diagMetierIds && diagMetierIds.length > 0) {
          setMetierIds(diagMetierIds)

          // Load metier names from API
          let metiersSource: any[] = []
          try {
            metiersSource = await api.getMetiers()
          } catch {
            // Use data from diagnostic's metiers if API fails
            metiersSource = (diagnostic.metiers || []).map((m: any) => m.metier || m)
          }

          // Construire les données avec les noms disponibles
          const metiersData: SelectedMetier[] = diagMetierIds.map((code: string, index: number) => {
            const metier = metiersSource.find((m: any) => m.code === code)
            return {
              code,
              nom: metier?.nom || code,
              ordre: index + 1,
            }
          })
          setMetiersSelectionnes(metiersData)
        }
      } catch (error) {
        console.error('Erreur chargement diagnostic depuis API:', error)
      } finally {
        setIsLoadingData(false)
      }
    }
    loadFromAPI()
  }, [diagnosticId, metierIds.length])

  // Construire le state de navigation
  const diagnosticState: DiagnosticNavigationState = {
    metierIds,
    metiersSelectionnes,
  }

  return {
    metierIds,
    metiersSelectionnes,
    diagnosticState,
    isLoadingData,
    // Setters pour permettre aux pages de mettre à jour les données si nécessaire
    setMetierIds,
    setMetiersSelectionnes,
  }
}
